import { useState } from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'

import PanesHeader from 'modules/core/components/PanesHeader'
import i18n from 'modules/core/lib/i18n'

import type Group from 'modules/reviews/store/models/Group'
import type QuestionGroupConfig from 'modules/reviews/store/models/QuestionGroupConfig'
import type Review from 'modules/reviews/store/models/v2/Review'

import QuestionConfigs from '../QuestionConfigs/QuestionConfigs'
import AddQuestionModalContainer from './AddQuestion/AddQuestionModalContainer'
import ApplyTemplateModal from './ApplyTemplateModal'
import NewCategoryModalContainer from './Categories/NewCategoryModalContainer'

function groupName(group: Group | null | undefined) {
  if (!group) return ''

  const types: Record<string, string> = {
    self: i18n.t('surveys.review.questions.question_group_config.self.title'),
    manager: i18n.t('surveys.review.questions.question_group_config.manager.title'),
    reportees: i18n.t('surveys.review.questions.question_group_config.reportees.title'),
    peers: i18n.t('surveys.review.questions.question_group_config.peers.title'),
    manual: i18n.t('surveys.review.questions.question_group_config.manual.title'),
  }

  return types[group.attributes.name]
}

function groupDescription(group: Group | null | undefined) {
  if (!group) return ''

  const types: Record<string, string> = {
    self: i18n.t('surveys.review.questions.question_group_config.self.description'),
    manager: i18n.t('surveys.review.questions.question_group_config.manager.description'),
    reportees: i18n.t('surveys.review.questions.question_group_config.reportees.description'),
    peers: i18n.t('surveys.review.questions.question_group_config.peers.description'),
    manual: i18n.t('surveys.review.questions.question_group_config.manual.description'),
  }

  return types[group.attributes.name]
}

interface Props {
  questionGroupConfig: QuestionGroupConfig
  form: Review
  hasPerformanceScoreQuestions: boolean
}

export function ReviewTypeItem({ questionGroupConfig, form, hasPerformanceScoreQuestions }: Props) {
  const [showNewQuestionModal, setShowNewQuestionModal] = useState(false)
  const [showNewSectionModal, setShowNewSectionModal] = useState(false)
  const [showApplyTemplateModal, setShowApplyTemplateModal] = useState(false)
  const reviewTypeId = questionGroupConfig.id
  const onCloseNewQuestionModal = () => {
    setShowNewQuestionModal(false)
  }
  const onCloseNewSectionModal = () => {
    setShowNewSectionModal(false)
  }
  const onCloseApplyTemplateModal = () => {
    setShowApplyTemplateModal(false)
  }

  if (!form) return null
  return (
    <Box gap="s48">
      <Box>
        <PanesHeader
          line={false}
          title={groupName(questionGroupConfig.respondent) ?? ''}
          description={groupDescription(questionGroupConfig.respondent)}
        />
        <Box flexDirection="row" gap="s16">
          <Button
            type="primary"
            onClick={() => {
              setShowApplyTemplateModal(true)
            }}
            iconName="duplicate"
          >
            {i18n.t('surveys.form.question_configs.use_template')}
          </Button>
          <Button
            type="secondary"
            onClick={() => {
              setShowNewSectionModal(true)
            }}
            iconName="align-text-justify"
          >
            {i18n.t('surveys.form.question_configs.add_section')}
          </Button>
          <Button
            type="secondary"
            onClick={() => {
              setShowNewQuestionModal(true)
            }}
            iconName="add"
          >
            {i18n.t('surveys.form.question_configs.add_question_config')}
          </Button>
        </Box>
      </Box>
      <Box gap="s24">
        <QuestionConfigs
          questionGroupConfig={questionGroupConfig}
          form={form}
          hasPerformanceScoreQuestions={hasPerformanceScoreQuestions}
        />
      </Box>

      {showNewQuestionModal && (
        <AddQuestionModalContainer
          reviewId={form.id}
          onClose={onCloseNewQuestionModal}
          questionGroupConfigId={reviewTypeId}
        />
      )}

      {showNewSectionModal && (
        <NewCategoryModalContainer
          formId={form.id}
          onClose={onCloseNewSectionModal}
          questionGroupConfigId={reviewTypeId}
        />
      )}

      {showApplyTemplateModal && (
        <ApplyTemplateModal
          reviewId={form.id}
          onClose={onCloseApplyTemplateModal}
          questionGroupConfigId={reviewTypeId}
        />
      )}
    </Box>
  )
}
